import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  fetchAppointments,
  fetchAppointment,
  createAppointmentRequest,
} from '../services/patientApi';
import { queryKeys } from '../queryKeys';

/** List of all the patient's appointments (used by the list view). */
export const useAppointmentsQuery = () =>
  useQuery({
    queryKey: queryKeys.appointments.list(),
    queryFn: fetchAppointments,
  });

/** Single appointment detail (used by the show page). Pauses while id is unset. */
export const useAppointmentQuery = (id: string | undefined) =>
  useQuery({
    queryKey: queryKeys.appointments.detail(id ?? ''),
    queryFn: () => fetchAppointment(id!),
    enabled: !!id,
  });

/**
 * Submit a "confirm" AppointmentRequest from the patient.
 *
 * This does NOT flip `appointment.confirmed` — it just files a pending
 * request that staff will review. On success we invalidate the appointment
 * caches so the new request is reflected in the badge / timeline / button
 * gating across every surface.
 *
 * Returns standard mutation state: `mutate(id)` to fire, `variables` is the
 * id currently in flight (or undefined when idle).
 */
export const useConfirmAppointmentMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => createAppointmentRequest(id, 'confirm'),
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: queryKeys.appointments.list() });
      qc.invalidateQueries({ queryKey: queryKeys.appointments.detail(id) });
      toast.success('Created request for appointment confirmation');
    },
  });
};
